'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, Padding, TextArea, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import {
  type UpdateCurationInfoServiceRequest,
  type UpdateCurationInfoServiceResponse,
  updateCurationInfoService,
} from '@musinsa/services-curator';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';
import { useCuratorBroadcastPublisher } from '../../common';
import { BottomButtonWrapper } from '../../common/components/BottomButtonWrapper';
import { useCuratorSnackbar } from '../../common/hooks';
import { useNavigate } from '../../common/hooks/useNavigate';
import { curatorQueries } from '../../query-factory';

// NOTE: 큐레이션 정보 폼 스키마 정의
const CurationInfoFormScheme = z.object({
  description: z.string().max(60, '소개는 60자 이하로 입력해주세요.'),
});

type CurationInfoFormData = z.infer<typeof CurationInfoFormScheme>;

interface Props {
  className?: string;
}

export const CurationInfoForm = ({ className }: Props) => {
  const { navigateBack } = useNavigate();
  const snackbar = useCuratorSnackbar();
  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  const { service } = useDeviceInfo();

  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const snsTitle = userInfo?.snsTitle ?? '';
  const { data: profile, isPending: isProfilePending } = useQuery({
    ...curatorQueries.profile.profileBySnsTitle(snsTitle),
    enabled: Boolean(snsTitle),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<CurationInfoFormData>({
    resolver: zodResolver(CurationInfoFormScheme),
    values: {
      description: profile?.shopDescription ?? '',
    },
  });

  const { mutate: updateCurationInfo, isPending } = useMutation<
    UpdateCurationInfoServiceResponse,
    Error,
    UpdateCurationInfoServiceRequest
  >({
    mutationFn: async (data: UpdateCurationInfoServiceRequest) => updateCurationInfoService(data),
    onSuccess: () => {
      if (service === SERVICE.MUSINSA && snsTitle) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'profile_edit_complete',
          component_name: 'button',
          event_name: 'click_button',
          from: getScreenName(),
          id: snsTitle,
          name: 'shop_description',
          section_name: 'curator_profile',
        });
      }
      snackbar.success('큐레이션 샵 소개가 수정되었습니다.');
      if (snsTitle) {
        triggerQuerySync({
          queryKeys: [curatorQueries.profile.profileBySnsTitle(snsTitle).queryKey],
        });
      }
      navigateBack();
    },
    onError: (error: Error) => {
      snackbar.error(error.message);
    },
  });

  const onFormSubmit = (data: CurationInfoFormData) => {
    updateCurationInfo(data);
  };

  return (
    <form
      className={twMerge(
        `flex flex-col justify-between w-full h-full grow bg-neutral-color-white`,
        className,
      )}
      onSubmit={handleSubmit(onFormSubmit)}
    >
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <div className="grow flex flex-col gap-[8px] px-[12px]! pt-[16px]! pb-[72px]!">
            <TextArea
              {...field}
              onChange={(e) => {
                // Android에서는 maxLength가 동작하지않아 이를 위해 60자 제한을 강제로 적용(https://jira.team.musinsa.com/browse/CMQA-5045)
                const value = e.target.value.slice(0, 60);
                field.onChange(value);
              }}
              placeholder="소개를 입력해주세요."
              isError={!!errors.description?.message}
              isLoading={isProfilePending}
              helperText={errors.description?.message}
              fullWidth
              className="h-[128px]"
              maxLength={60}
              disabled={isProfilePending}
            />
            <div className="flex justify-end">
              <Typography
                as="p"
                size="14"
                weight="regular"
                colorScheme="gray-500"
              >{`${field.value?.length ?? 0}/60`}</Typography>
            </div>
          </div>
        )}
      />

      <BottomButtonWrapper>
        <Padding y="3">
          <Button
            size="44"
            className="w-full"
            variant="primary"
            isLoading={isSubmitting || isPending || isProfilePending}
            disabled={!isDirty}
          >
            <Typography size="14" weight="medium">
              수정하기
            </Typography>
          </Button>
        </Padding>
      </BottomButtonWrapper>
    </form>
  );
};
